import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  Send,
  Copy,
  Check,
  Building2,
  User,
  Phone,
  FileText,
  Share2,
  X,
  ExternalLink,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';

export interface WhatsAppShareData {
  title: string;
  docType:
    | 'DOCUMENT'
    | 'QUOTATION'
    | 'INVOICE'
    | 'CONSOLIDATED_INVOICE'
    | 'COMPLETION_CERT'
    | 'SITE_SURVEY'
    | 'PURCHASE_ORDER'
    | 'BOQ';
  referenceNumber?: string;
  projectName?: string;
  branchName?: string;
  branchCode?: string;
  amount?: string;
  date?: string;
  recipientName?: string;
  recipientPhone?: string;
  detailsSummary?: string;
  fileUrl?: string;
}

interface WhatsAppShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: WhatsAppShareData | null;
}

const PRESET_CONTACTS = [
  {
    category: 'UBL Representatives',
    contacts: [
      { name: 'Engr. Imran Qureshi', role: 'UBL Regional Facility Manager (Lahore)', phone: '+923218456789' },
      { name: 'Tariq Mehmood', role: 'Branch Manager (UBL Mall Road # 0142)', phone: '+923004567890' },
      { name: 'Engr. Asim Raza', role: 'Corporate Premises Engineer (UBL North)', phone: '+923335567891' },
      { name: 'UBL Central Accounts Desk', role: 'Finance & Premises Disbursals', phone: '+923001234567' },
    ],
  },
  {
    category: 'Naeem Builder Management & Sites',
    contacts: [
      { name: 'Naeem Akhtar', role: 'Managing Director • Naeem Builder', phone: '+923008421199' },
      { name: 'Engr. Salman Farooq', role: 'Senior Project Manager', phone: '+923334219988' },
      { name: 'Engr. Bilal Hashmi', role: 'Lead Site Engineer', phone: '+923017766554' },
      { name: 'NB Accounts & Billing Desk', role: 'Head Office Accounts', phone: '+923229988771' },
    ],
  },
  {
    category: 'Key Suppliers & Vendors',
    contacts: [
      { name: 'Mian Shafi', role: 'Bestway Cement & Aggregates Supply', phone: '+923007654321' },
      { name: 'Al-Raziq Hardware & Paints', role: 'Master Tiles / Berger Paints Distributor', phone: '+923029876543' },
    ],
  },
];

export const WhatsAppShareModal: React.FC<WhatsAppShareModalProps> = ({
  isOpen,
  onClose,
  data,
}) => {
  const [selectedContact, setSelectedContact] = useState<string>('custom');
  const [phone, setPhone] = useState<string>('+92');
  const [contactName, setContactName] = useState<string>('');
  const [templateType, setTemplateType] = useState<'FORMAL' | 'BILINGUAL' | 'QUICK'>('FORMAL');
  const [message, setMessage] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);

  // Generate message based on template & data
  useEffect(() => {
    if (!data) return;

    // Default recipient if provided
    if (data.recipientPhone) {
      setPhone(data.recipientPhone);
    }
    if (data.recipientName) {
      setContactName(data.recipientName);
    }

    const currentUrl = typeof window !== 'undefined' ? window.location.origin : 'https://naeembuilder.com';
    const docLink = data.fileUrl ? `${currentUrl}${data.fileUrl}` : `${currentUrl}/documents`;

    const branchInfo = data.branchName ? `${data.branchName}${data.branchCode ? ` (${data.branchCode})` : ''}` : '';
    const refText = data.referenceNumber ? `• Reference #: *${data.referenceNumber}*` : '';
    const prjText = data.projectName ? `• Project: *${data.projectName}*` : '';
    const brText = branchInfo ? `• UBL Branch: *${branchInfo}*` : '';
    const amtText = data.amount ? `• Amount / Value: *${data.amount}*` : '';
    const dateText = data.date ? `• Date: *${data.date}*` : `• Date: *${new Date().toLocaleDateString('en-PK')}*`;
    const details = data.detailsSummary ? `• Scope / Notes: _${data.detailsSummary}_` : '';

    let generated = '';

    if (templateType === 'FORMAL') {
      generated = `*NAEEM BUILDER — OFFICIAL DISPATCH*\n_Project Services & Construction for United Bank Limited (UBL)_\n\nRespected Sir/Madam,\n\nPlease find attached the official project document details:\n\n📄 *Document:* ${data.title}\n${refText}\n${prjText}\n${brText}\n${amtText}\n${dateText}\n${details}\n\n🔗 *Access Document Online:*\n${docLink}\n\nKindly acknowledge receipt. For any clarification or verification, please contact Naeem Builder Project Coordination at +92-42-35889900.\n\n*Naeem Builder (Pvt.) Ltd.*\nLahore • Karachi • Islamabad`;
    } else if (templateType === 'BILINGUAL') {
      generated = `*NAEEM BUILDER — UBL PROJECT UPDATE / سائٹ نوٹس*\n\nمحترم جناب،\nمندرجہ ذیل یو بی ایل پروجیکٹ کے ضروری دستاویزات روانہ کیے جا رہے ہیں:\n\n📋 *File:* ${data.title}\n${refText}\n${prjText}\n${brText}\n${amtText}\n${details}\n\n🌐 *Online Link:* ${docLink}\n\nبرائے مہربانی چیک کر کے توثیق فرمائیں۔\n\n*Naeem Builder Team*`;
    } else {
      // QUICK
      generated = `*Naeem Builder Dispatch:* ${data.title}\n${refText ? `${refText}\n` : ''}${brText ? `${brText}\n` : ''}${amtText ? `${amtText}\n` : ''}Link: ${docLink}`;
    }

    setMessage(generated);
  }, [data, templateType]);

  if (!isOpen || !data) return null;

  const handleSelectPreset = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setSelectedContact(val);

    if (val === 'custom') {
      return;
    }

    for (const group of PRESET_CONTACTS) {
      const match = group.contacts.find((c) => c.phone === val);
      if (match) {
        setPhone(match.phone);
        setContactName(match.name);
        return;
      }
    }
  };

  const cleanPhoneNumber = (num: string) => {
    // Keep numbers only
    const cleaned = num.replace(/[^0-9]/g, '');
    // If starts with 03, replace with 923
    if (cleaned.startsWith('03')) {
      return '92' + cleaned.substring(1);
    }
    // If starts with 92, return as is
    if (cleaned.startsWith('92')) {
      return cleaned;
    }
    // If starts with 3, prepend 92
    if (cleaned.startsWith('3') && cleaned.length === 10) {
      return '92' + cleaned;
    }
    return cleaned;
  };

  const handleSendWhatsApp = async () => {
    const cleanedPhone = cleanPhoneNumber(phone);
    if (!cleanedPhone || cleanedPhone.length < 10) {
      alert('Please enter a valid Pakistani phone number (+92 3XX XXXXXXX)');
      return;
    }

    setIsSending(true);

    try {
      // Audit log on backend
      await fetch('/api/documents/share-whatsapp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipientName: contactName || 'Client / Contact',
          recipientPhone: phone,
          documentTitle: data.title,
          documentType: data.docType,
          referenceNumber: data.referenceNumber,
          projectName: data.projectName,
          branchName: data.branchName,
        }),
      }).catch(() => {
        // Continue even if logging endpoint fails
      });

      setSendSuccess(true);

      // Safe anchor link opening (avoid window.open)
      const encodedText = encodeURIComponent(message);
      const waUrl = `https://wa.me/${cleanedPhone}?text=${encodedText}`;

      const link = document.createElement('a');
      link.href = waUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => {
        setSendSuccess(false);
      }, 3500);
    } finally {
      setIsSending(false);
    }
  };

  const handleCopyMessage = () => {
    navigator.clipboard.writeText(message);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleCopyLink = () => {
    const currentUrl = typeof window !== 'undefined' ? window.location.origin : 'https://naeembuilder.com';
    const docLink = data.fileUrl ? `${currentUrl}${data.fileUrl}` : `${currentUrl}/documents`;
    navigator.clipboard.writeText(docLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2500);
  };

  const getDocTypeBadge = (type: string) => {
    switch (type) {
      case 'QUOTATION':
        return { label: 'Quotation', bg: 'bg-blue-100 text-blue-900 border-blue-200' };
      case 'INVOICE':
        return { label: 'Invoice', bg: 'bg-emerald-100 text-emerald-900 border-emerald-200' };
      case 'CONSOLIDATED_INVOICE':
        return { label: 'Consolidated Invoice', bg: 'bg-amber-100 text-amber-900 border-amber-300' };
      case 'COMPLETION_CERT':
        return { label: 'Completion Certificate', bg: 'bg-purple-100 text-purple-900 border-purple-200' };
      case 'SITE_SURVEY':
        return { label: 'Site Survey', bg: 'bg-sky-100 text-sky-900 border-sky-200' };
      case 'PURCHASE_ORDER':
        return { label: 'Purchase Order', bg: 'bg-indigo-100 text-indigo-900 border-indigo-200' };
      case 'BOQ':
        return { label: 'BOQ Estimation', bg: 'bg-teal-100 text-teal-900 border-teal-200' };
      default:
        return { label: 'Project Document', bg: 'bg-slate-100 text-slate-900 border-slate-200' };
    }
  };

  const badge = getDocTypeBadge(data.docType);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-xs animate-in fade-in duration-150">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-xl w-full max-h-[92vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header - WhatsApp Green Accent */}
        <div className="bg-[#0b5c46] text-white p-4 sm:p-5 flex items-center justify-between relative overflow-hidden">
          <div className="absolute -right-6 -bottom-6 w-28 h-28 bg-[#25D366]/20 rounded-full blur-xl pointer-events-none" />
          <div className="flex items-center gap-3 relative z-10">
            <div className="w-10 h-10 rounded-xl bg-[#25D366] text-white flex items-center justify-center shadow-md shrink-0">
              <MessageSquare className="w-5 h-5 fill-current" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-base sm:text-lg tracking-tight">Share via WhatsApp</h3>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#25D366]/30 text-emerald-100 border border-[#25D366]/40">
                  Instant Dispatch
                </span>
              </div>
              <p className="text-xs text-emerald-100/90 mt-0.5">
                Send official project documents to UBL branch engineers, managers &amp; vendors.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="text-emerald-100 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-4 sm:p-5 space-y-4 overflow-y-auto text-xs">
          {/* Document Summary Card */}
          <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200/80 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="space-y-0.5">
                <span
                  className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded-md border ${badge.bg}`}
                >
                  {badge.label}
                </span>
                <h4 className="font-bold text-slate-900 text-sm">{data.title}</h4>
              </div>

              {data.amount && (
                <div className="text-right shrink-0">
                  <span className="text-[10px] text-slate-400 block font-medium">Value</span>
                  <span className="text-sm font-bold text-emerald-700">{data.amount}</span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[11px] text-slate-600 pt-1 border-t border-slate-200/60">
              {data.referenceNumber && (
                <div>
                  <span className="text-slate-400">Ref #:</span>{' '}
                  <span className="font-semibold text-slate-800">{data.referenceNumber}</span>
                </div>
              )}
              {data.projectName && (
                <div className="truncate">
                  <span className="text-slate-400">Project:</span>{' '}
                  <span className="font-semibold text-slate-800">{data.projectName}</span>
                </div>
              )}
              {data.branchName && (
                <div className="truncate">
                  <span className="text-slate-400">UBL Branch:</span>{' '}
                  <span className="font-semibold text-slate-800">{data.branchName}</span>
                </div>
              )}
              {data.detailsSummary && (
                <div className="sm:col-span-2 truncate">
                  <span className="text-slate-400">Summary:</span>{' '}
                  <span className="italic text-slate-700">{data.detailsSummary}</span>
                </div>
              )}
            </div>
          </div>

          {/* Quick Recipient Directory */}
          <div className="space-y-1.5">
            <label className="block font-bold text-slate-800 flex items-center justify-between">
              <span>Select Recipient / Contact</span>
              <span className="text-[10px] text-slate-400 font-normal">Presets or enter any Pakistan phone</span>
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <select
                  value={selectedContact}
                  onChange={handleSelectPreset}
                  className="w-full border border-slate-300 rounded-lg p-2 text-xs bg-white text-slate-800 focus:border-[#0b5c46] focus:outline-hidden font-medium"
                >
                  <option value="custom">-- Custom Phone Number --</option>
                  {PRESET_CONTACTS.map((grp) => (
                    <optgroup key={grp.category} label={grp.category}>
                      {grp.contacts.map((c) => (
                        <option key={c.phone} value={c.phone}>
                          {c.name} ({c.role.split('(')[0].trim()})
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-slate-400">
                    <Phone className="w-3.5 h-3.5 text-emerald-600" />
                  </div>
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+92 300 1234567"
                    className="w-full pl-8 pr-2.5 py-2 border border-slate-300 rounded-lg text-xs font-mono font-semibold text-slate-800 focus:border-[#0b5c46] focus:outline-hidden"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Message Format / Language Template */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="font-bold text-slate-800">Message Style &amp; Format</label>
              <div className="flex items-center gap-1 bg-slate-100 p-0.5 rounded-lg border border-slate-200">
                <button
                  type="button"
                  onClick={() => setTemplateType('FORMAL')}
                  className={`px-2.5 py-1 rounded text-[11px] font-semibold transition-colors ${
                    templateType === 'FORMAL'
                      ? 'bg-white text-slate-900 shadow-2xs'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Corporate (English)
                </button>
                <button
                  type="button"
                  onClick={() => setTemplateType('BILINGUAL')}
                  className={`px-2.5 py-1 rounded text-[11px] font-semibold transition-colors ${
                    templateType === 'BILINGUAL'
                      ? 'bg-white text-slate-900 shadow-2xs'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Site Notice (Urdu + Eng)
                </button>
                <button
                  type="button"
                  onClick={() => setTemplateType('QUICK')}
                  className={`px-2.5 py-1 rounded text-[11px] font-semibold transition-colors ${
                    templateType === 'QUICK'
                      ? 'bg-white text-slate-900 shadow-2xs'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Quick Alert
                </button>
              </div>
            </div>

            {/* Editable WhatsApp Text Preview */}
            <div className="relative">
              <textarea
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full border border-slate-300 rounded-xl p-3 text-[11px] font-mono leading-relaxed bg-[#f8faf9] text-slate-800 focus:bg-white focus:border-[#0b5c46] focus:outline-hidden resize-none"
              />
              <div className="absolute right-2 bottom-2.5 flex items-center gap-1">
                <button
                  type="button"
                  onClick={handleCopyMessage}
                  className="px-2 py-1 bg-white hover:bg-slate-50 border border-slate-200 rounded text-[10px] font-semibold text-slate-700 flex items-center gap-1 shadow-2xs"
                  title="Copy WhatsApp text"
                >
                  {copied ? (
                    <>
                      <Check className="w-3 h-3 text-emerald-600" />
                      <span className="text-emerald-700">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3 text-slate-500" />
                      <span>Copy Text</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="px-2 py-1 bg-white hover:bg-slate-50 border border-slate-200 rounded text-[10px] font-semibold text-slate-700 flex items-center gap-1 shadow-2xs"
                  title="Copy direct portal link"
                >
                  {linkCopied ? (
                    <>
                      <Check className="w-3 h-3 text-emerald-600" />
                      <span className="text-emerald-700">Link Copied!</span>
                    </>
                  ) : (
                    <>
                      <ExternalLink className="w-3 h-3 text-slate-500" />
                      <span>Copy Link</span>
                    </>
                  )}
                </button>
              </div>
            </div>
            <p className="text-[10px] text-slate-400">
              You can edit the message above directly before dispatching. WhatsApp formatting (*bold*, _italic_) is supported.
            </p>
          </div>

          {sendSuccess && (
            <div className="bg-emerald-50 border border-emerald-200 p-2.5 rounded-lg flex items-center gap-2 text-emerald-800 text-xs animate-in fade-in">
              <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>
                Dispatched to WhatsApp Web / App! Event recorded in Naeem Builder Audit Trail.
              </span>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 text-[11px] text-slate-500 self-start sm:self-auto">
            <span className="w-2 h-2 rounded-full bg-[#25D366] inline-block animate-pulse" />
            <span>Ready to dispatch via WhatsApp Web or Mobile</span>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-white text-xs font-semibold flex-1 sm:flex-initial"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleSendWhatsApp}
              disabled={isSending}
              className="flex items-center justify-center gap-2 px-5 py-2.5 bg-[#25D366] hover:bg-[#20bd5a] active:bg-[#1caa51] text-white font-bold text-xs rounded-lg shadow-sm transition-all flex-1 sm:flex-initial"
            >
              <Send className="w-3.5 h-3.5" />
              <span>{isSending ? 'Launching WhatsApp...' : 'Send on WhatsApp'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
